import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import AIAssistant from "@/components/AIAssistant";
import { supabase } from "@/integrations/supabase/client";
import { FAQItem } from "@/types";
import { Loader2, Sparkles } from "lucide-react";
import usePageSEO from "@/hooks/usePageSEO";
import { Link } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Helmet } from "react-helmet";

const FAQ = () => {
  usePageSEO({
    title: "FAQ - Questions fréquentes | Bio Beauté Zen",
    description: "Questions fréquentes sur Bio Beauté Zen : informations sur nos soins, rendez-vous, produits bio et tarifs de l'institut à Montpellier.",
    canonical: "https://biobeautezen.com/questions-frequentes-institut-beaute",
    ogTitle: "FAQ | Bio Beauté Zen Montpellier",
    ogDescription: "Trouvez les réponses à vos questions sur nos soins, rendez-vous, produits bio et tarifs."
  });

  const [searchTerm, setSearchTerm] = useState("");
  
  const { data: faqItems = [], isLoading } = useQuery({
    queryKey: ["faq-items"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("faq_items")
        .select("*")
        .eq("is_active", true)
        .order("category", { ascending: true })
        .order("order_index", { ascending: true });
      
      if (error) throw error;
      return (data || []) as FAQItem[];
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  
  const filteredFAQs = searchTerm ? faqItems.filter(item => item.question.toLowerCase().includes(searchTerm.toLowerCase()) || item.answer.toLowerCase().includes(searchTerm.toLowerCase()) || item.category.toLowerCase().includes(searchTerm.toLowerCase())) : faqItems;
  
  const categoryGroups: Record<string, FAQItem[]> = {};
  filteredFAQs.forEach(item => {
    if (!categoryGroups[item.category]) {
      categoryGroups[item.category] = [];
    }
    categoryGroups[item.category].push(item);
  });
  
  if (isLoading) {
    return <main className="container mx-auto px-4 py-24 md:py-32" role="main">
        <div className="flex justify-center items-center min-h-[50vh]">
          <Loader2 className="h-8 w-8 animate-spin text-bbz-green" />
          <span className="ml-3 text-lg">Chargement des FAQ...</span>
        </div>
      </main>;
  }
  
  return <>
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Accueil",
              "item": "https://biobeautezen.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "FAQ",
              "item": "https://biobeautezen.com/questions-frequentes-institut-beaute"
            }
          ]
        })}
      </script>
    </Helmet>
    <div className="pt-20 pb-4">
      <div className="container mx-auto px-4">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Accueil</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>FAQ</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>
    </div>
    <main className="container mx-auto px-4 pb-16" role="main">
      <header className="text-center mb-12">
        <h1 className="section-title">Questions fréquentes</h1>
      </header>

      <section aria-labelledby="faq-search" className="max-w-2xl mx-auto mb-10">
        <label htmlFor="faq-search" className="sr-only">Rechercher une question</label>
        <input id="faq-search" type="text" placeholder="Rechercher une question..." className="w-full px-4 py-2 border border-bbz-green/50 rounded-md focus:outline-none focus:ring-2 focus:ring-bbz-green" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
      </section>

      {Object.keys(categoryGroups).length === 0 ? <div className="text-center py-8">
          <p className="text-bbz-text text-lg">
            Aucune question ne correspond à votre recherche. Veuillez essayer avec d'autres termes.
          </p>
        </div> : <section aria-labelledby="faq-list" className="max-w-3xl mx-auto space-y-10">
          {Object.entries(categoryGroups).map(([category, items]) => <div key={category}>
              <h2 className="text-xl font-semibold mb-4 text-bbz-text">{category}</h2>
              <Accordion type="single" collapsible className="space-y-4">
                {items.map((item, index) => <AccordionItem key={index} value={`item-${category}-${index}`} className="border border-bbz-green/30 rounded-lg overflow-hidden">
                    <AccordionTrigger className="px-4 py-3 bg-bbz-cream/50 hover:bg-bbz-cream/70 text-bbz-text text-left font-medium">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="px-4 py-3 text-bbz-text bg-white">
                      <p>{item.answer}</p>
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>)}
      </section>}
    </main>
  </>;
};

export default FAQ;
